import React, { useState, useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import "../../styles/armygraph.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
const languageArray = [
  "",
"Hindi",
"Kannada",
"Tamil",
"Telugu",
"Malayalam"
];

const ageArray = [
  "",
  "18-35",
  "36-45",
  "46-55",
  "56-65",
];

// Sample data format (replace with your API or prop)
// const rawData = [
//   { issue: "Anxiety", data: 50, year: 2025, month: "August" },
//   { issue: "Depression", data: 32, year: 2025, month: "August" },
//   { issue: "Stress", data: 24, year: 2025, month: "August" },
//   { issue: "Trauma", data: 18, year: 2025, month: "August" },
//   { issue: "Anxiety", data: 44, year: 2025, month: "September" },
//   { issue: "Depression", data: 38, year: 2025, month: "September" },
//   { issue: "Stress", data: 29, year: 2025, month: "September" },
//   { issue: "Trauma", data: 10, year: 2025, month: "September" },
//   // ...add more as needed
// ];

function filterSoldiers(
  dataArray: any[],
  { ageFilter = "", language = "" } = {}
) {
  return dataArray.filter((soldier) => {
    const age = Number(soldier.Age);
    const lang = soldier["Assessment Language"]?.toLowerCase() || "";

    let ageMatch = true;
    let langMatch = true;

    // ✅ Handle age filter input (like "<18", "45-50", ">35")
    if (ageFilter) {
      const cleaned = ageFilter.replace(/\s+/g, "");

      if (/^<\d+$/.test(cleaned)) {
        // e.g. "<18"
        const limit = Number(cleaned.slice(1));
        ageMatch = age < limit;
      } else if (/^>\d+$/.test(cleaned)) {
        // e.g. ">35"
        const limit = Number(cleaned.slice(1));
        ageMatch = age > limit;
      } else if (/^\d+-\d+$/.test(cleaned)) {
        // e.g. "45-50"
        const [min, max] = cleaned.split("-").map(Number);
        ageMatch = age >= min && age <= max;
      } else {
        // invalid input -> don't filter by age
        ageMatch = true;
      }
    }

    // ✅ Handle language filter (case-insensitive)
    if (language) {
      langMatch = lang === language.toLowerCase();
    }

    return ageMatch && langMatch;
  });
}
type SeverityGroups = {
  None: string[];
  Slight: string[];
  Mild: string[];
  Moderate: string[];
  High: string[];
};
function transformData(data: any[]) {
   const issueCounts :any= {};

  data.forEach((soldier) => {
    const { Mild = [], Moderate = [], High = [] } = soldier.SeverityGroups || {};

    // Combine only Mild, Moderate, and High
    const relevantIssues = [...Mild, ...Moderate, ...High];

    relevantIssues.forEach((issue) => {
      issueCounts[issue] = (issueCounts[issue] || 0) + 1;
    });
  });

  // Convert the result into the desired array format
  return Object.entries(issueCounts)
  .map(([issue, count]) => ({ issue, count }))
  .sort((a:any, b:any) => b.count - a.count);;
}


const colorPalette = [
  "#8fd3f4",
  "#84fab0",
  "#f093fb",
  "#f5576c",
  "#f1c40f",
  "#fd6e6a",
  "#a18cd1",
  "#43e97b",
];

// const legendLabels = [
//   {
//     label: "Emotional Issues",
//     color: "#8fd3f4",
//     desc: "Includes anxiety, depression, stress and trauma",
//   },
//   {
//     label: "Behavioral Issues",
//     color: "#f5576c",
//     desc: "Includes phobias, addiction, burnout",
//   },
// ];

const DashboardBarGraph = ({data}:any) => {
  // years and months are auto-discovered from data
  
  // const [selectedYear, setSelectedYear] = useState(years[0]);
  const [rawData, setRawData] = useState(transformData(data));
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  // const months = useMemo(
  //   () =>
  //     Array.from(
  //       new Set(
  //         rawData.filter((e) => e.year === selectedYear).map((e) => e.month)
  //       )
  //     ),
  //   [selectedYear]
  // );
  // const [selectedMonth, setSelectedMonth] = useState(months[0] || "");

  // When selected year changes, auto-reset month
 
useEffect(() => {
  setRawData(transformData(filterSoldiers(data, { ageFilter: selectedAge, language: selectedLanguage })));
}, [selectedAge, selectedLanguage]);
  // Filtered data for chart
  // const filteredData = rawData.filter(
  //   (e) => e.year === selectedYear && e.month === selectedMonth
  // );

  const chartData = {
    labels: rawData.map((e:any) => e.issue.replace(/_/g, " ")),
    datasets: [
      {
        label: "Cases",
        data: rawData.map((e:any) => e.count),
        backgroundColor: "rgb(232,105,74)", 
        borderRadius: 15,
        maxBarThickness: 54,
      },
    ],
  };

  const options = {
    responsive: true,
    // maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "#fff",
        borderColor: "#e5e9f6",
        borderWidth: 1.5,
        borderRadius: 14,
        padding: 8,
        titleColor: "#5275fd",
        bodyColor: "#3d4664",
        bodyFont: {
          size: 16,
          weight: 500,
          family: "Poppins, Segoe UI, sans-serif",
        },
        displayColors: false,
        callbacks: {
          title: () => "",
          label: (ctx: any) => `${ctx.raw} Cases`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { font: { size: 15 }, color: "#303060" },
      },
      y: {
        grid: { display: false, drawBorder: false },
        ticks: { font: { size: 13 } },
      },
    },
  };
  // x: { grid: { display: false }, ticks: { font: { size: 16 } } },
  // y: { grid: { color: "#f2f4fa" }, ticks: { font: { size: 13 } } },

  return (
    <div className="modern-graph-card">
      <div className="modern-graph-top modern-select-row">
        <select
          className="modern-select"
          value={selectedAge}
          onChange={(e) => setSelectedAge(e.target.value)}
        >
          {ageArray.map((y) => (
            <option key={y} value={y}>
              {y=== "" ? "Select Age" : y}
            </option>
          ))}
        </select>
        <select
          className="modern-select"
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
        >
          {languageArray.map((m) => (
            <option key={m} value={m}>
              {m === "" ? "Select Language" : m}
            </option>
          ))}
        </select>
      </div>
      <h2 className="modern-graph-title">Issue Distribution from Assessment</h2>
      <Bar data={chartData} options={options as any} />
      <div className="modern-graph-legend-row">
        {/* {legendLabels.map((legend) => ( */}
        {/* // <div className="modern-graph-legend-card" key={legend.label}> */}
        {/* <div className="legend-dot" style={{ background: legend.color }} /> */}
        {/* <span className="legend-title">{legend.label}</span> */}
        {/* <div className="legend-desc">{legend.desc}</div> */}
        {/* </div> */}
        {/* ))} */}
      </div>
      <div className="modern-graph-disclaimer">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="disclaimer-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M12 18a6 6 0 110-12 6 6 0 010 12z"
          />
        </svg>
        <small>
          Disclaimer: The data reflects soldiers’ self-reported responses, and our assessment indicates that the information may not be entirely accurate. It’s important that the next assessment is taken more seriously to ensure reliability. However, it remains our responsibility to present the data exactly as reported. The figures related to Mania, Psychosis, and Substance Abuse appear to be particularly inconsistent and should be interpreted with caution..
        </small>
      </div>
    </div>
  );
};

export default DashboardBarGraph;
